import { ArrowRight, CheckCircle2, Gift, Loader2, Wallet } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useWaitForTransactionReceipt, useWriteContract } from "wagmi";
import { getAddress, parseEther, zeroAddress } from "viem";
import { contractAddresses } from "../contracts/addresses";
import { referralRouterAbi } from "../contracts/abis";
import { useReferral } from "../hooks/useReferral";
import { REFERRAL_COPY, REFERRAL_HOLD_HOURS, REFERRAL_MIN_BUY_LABEL, REFERRAL_REWARD_BPS } from "../lib/referral";

const SLIPPAGE_OPTIONS = [2, 5, 10] as const;
const DEFAULT_SLIPPAGE = 5;
const DEADLINE_SECONDS = 600;

function truncateAddress(value: string): string {
  if (value.length <= 12) return value;
  try {
    const checksum = getAddress(value);
    return `${checksum.slice(0, 6)}…${checksum.slice(-4)}`;
  } catch {
    return `${value.slice(0, 6)}…${value.slice(-4)}`;
  }
}

function parseBnbAmount(input: string): bigint | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  if (!/^\d*(\.\d*)?$/.test(trimmed)) return null;
  try {
    const wei = parseEther(trimmed as `${number}`);
    if (wei <= 0n) return null;
    return wei;
  } catch {
    return null;
  }
}

export function ReferralBuyPanel() {
  const referral = useReferral();
  const router = contractAddresses.referralRouter;

  const [amount, setAmount] = useState("");
  const [slippage, setSlippage] = useState<number>(DEFAULT_SLIPPAGE);

  const { writeContract, data: txHash, isPending, error: writeError, reset } = useWriteContract();
  const { isLoading: isMining, isSuccess: isMined } = useWaitForTransactionReceipt({ hash: txHash });

  const parsedAmount = useMemo(() => parseBnbAmount(amount), [amount]);
  const hasReferrer =
    referral.storedReferrer && referral.storedReferrer.toLowerCase() !== zeroAddress.toLowerCase();

  const handleBuy = useCallback(() => {
    if (!router) return;
    if (parsedAmount === null) return;

    const deadline = BigInt(Math.floor(Date.now() / 1000) + DEADLINE_SECONDS);
    // v1: minTokenOut=0; Pancake's swap with fee-on-transfer support fills at market.
    // Slippage selector is captured for future quote-derived bounds; intentionally
    // not enforced yet to keep buys reliable for fee-on-transfer tokens.
    const minTokenOut = 0n;

    writeContract({
      address: router,
      abi: referralRouterAbi,
      functionName: "buyWithBNB",
      args: [referral.storedReferrer, minTokenOut, deadline],
      value: parsedAmount,
    });
  }, [parsedAmount, referral.storedReferrer, router, writeContract]);

  if (!referral.enabled) return null;
  if (!router) return null;

  const buyDisabled = parsedAmount === null || isPending || isMining;
  const errorMessage =
    writeError instanceof Error ? writeError.message : writeError ? String(writeError) : null;

  return (
    <section className="glass p-5 lg:p-6 relative overflow-hidden">
      <div
        className="pointer-events-none absolute -top-24 -left-16 w-72 h-72 rounded-full bg-bnb/10 blur-3xl"
        aria-hidden
      />

      <header className="flex items-center gap-2.5 mb-2 relative">
        <Gift className="w-5 h-5 text-bnb" strokeWidth={1.75} aria-hidden />
        <h3 className="text-sm font-mono uppercase tracking-wider text-ink">官方推荐买入</h3>
        <span className="ml-auto inline-flex items-center gap-1.5 rounded-chip border border-bnb/30 bg-bnb/10 px-2 py-1 text-[10px] font-mono uppercase tracking-wider text-bnb">
          BNB → 蜂鸟
        </span>
      </header>

      <p className="text-xs text-ink-dim leading-relaxed mb-4 max-w-2xl">
        {REFERRAL_COPY.rewardLine}。{REFERRAL_COPY.requirementLine}。
      </p>

      <div className="grid gap-4 lg:grid-cols-[1.1fr_1fr]">
        {/* Left column: referrer state + requirements */}
        <div className="space-y-3">
          {hasReferrer ? (
            <div className="rounded-chip border border-wing/20 bg-wing/[0.06] px-3 py-2.5 flex items-center gap-2.5">
              <CheckCircle2 className="w-4 h-4 text-wing shrink-0" strokeWidth={1.75} aria-hidden />
              <div className="min-w-0 flex-1">
                <div className="text-[10px] font-mono uppercase tracking-wider text-wing">
                  来自邀请人
                </div>
                <div className="text-xs font-mono tabular-nums text-ink truncate">
                  {truncateAddress(referral.storedReferrer)}
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-chip border border-white/10 bg-white/[0.02] px-3 py-2.5 flex items-center gap-2.5">
              <Wallet className="w-4 h-4 text-ink-dim shrink-0" strokeWidth={1.75} aria-hidden />
              <div className="text-xs text-ink-dim">暂无邀请人，仍可正常买入</div>
            </div>
          )}

          <ul className="space-y-1.5 text-xs text-ink-dim">
            <li className="flex items-baseline gap-2">
              <ArrowRight className="w-3 h-3 text-wing shrink-0 translate-y-0.5" strokeWidth={2} aria-hidden />
              <span>
                单笔买入 ≥{" "}
                <span className="font-mono text-ink">{REFERRAL_MIN_BUY_LABEL}</span>
              </span>
            </li>
            <li className="flex items-baseline gap-2">
              <ArrowRight className="w-3 h-3 text-wing shrink-0 translate-y-0.5" strokeWidth={2} aria-hidden />
              <span>
                持币满{" "}
                <span className="font-mono text-ink">{REFERRAL_HOLD_HOURS} 小时</span>{" "}
                后邀请人可领{" "}
                <span className="font-mono text-wing">{REFERRAL_REWARD_BPS / 100}%</span>{" "}
                奖励
              </span>
            </li>
          </ul>
        </div>

        {/* Right column: buy form */}
        <div className="rounded-card border border-white/[0.06] bg-white/[0.02] p-4 space-y-3">
          <div>
            <label
              htmlFor="referral-bnb-amount"
              className="text-[10px] font-mono uppercase tracking-wider text-ink-dim block mb-1.5"
            >
              支付 BNB
            </label>
            <div className="relative">
              <input
                id="referral-bnb-amount"
                type="text"
                inputMode="decimal"
                placeholder="0.0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full rounded-chip border border-white/10 bg-base/40 px-3 py-3 pr-16 text-lg font-mono tabular-nums text-ink placeholder:text-ink-dim/40 focus:outline-none focus:border-bnb/40 transition"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-mono uppercase tracking-wider text-bnb">
                BNB
              </span>
            </div>
          </div>

          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-ink-dim mb-1.5">
              滑点容忍
            </div>
            <div className="flex gap-1.5">
              {SLIPPAGE_OPTIONS.map((opt) => {
                const active = opt === slippage;
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setSlippage(opt)}
                    className={`flex-1 rounded-chip border px-2 py-1.5 text-xs font-mono tabular-nums transition ${
                      active
                        ? "border-bnb/40 bg-bnb/10 text-bnb"
                        : "border-white/10 bg-white/[0.02] text-ink-dim hover:text-ink hover:border-white/20"
                    }`}
                  >
                    {opt}%
                  </button>
                );
              })}
            </div>
          </div>

          <div className="text-[10px] font-mono text-ink-dim leading-relaxed">
            实际成交以钱包确认为准
          </div>

          <button
            type="button"
            onClick={handleBuy}
            disabled={buyDisabled}
            className="btn-bnb w-full inline-flex items-center justify-center gap-2"
          >
            {isPending || isMining ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} aria-hidden />
                {isPending ? "签名中…" : "上链中…"}
              </>
            ) : (
              <>买入 蜂鸟</>
            )}
          </button>

          {isMined ? (
            <p className="text-xs font-mono text-leaf flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
              买入成功
            </p>
          ) : null}
          {errorMessage ? (
            <p className="text-xs font-mono text-ember break-all">
              {errorMessage}
              <button
                type="button"
                onClick={reset}
                className="ml-2 underline decoration-dotted hover:text-ink"
              >
                关闭
              </button>
            </p>
          ) : null}
        </div>
      </div>
    </section>
  );
}
